import { useDebounce } from "@react-hooks-library/core";
import React, {
  Context,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from "react";

export type ReviewsContextProps = {
  addProductToQueue?: (handle: string) => void;
  productQueue?: readonly string[];
  totalReviewsCount?: number;
  setTotalReviewsCount?: (count: number) => void;
  renderRatingSnippet?: () => void;
};

export const ReviewsContext: Context<ReviewsContextProps> =
  React.createContext<ReviewsContextProps>({});

// This was previously in the config file, but only used here.
const config = {
  store: "bared.",
  snippet: {
    color: "#ba5c27",
    name: "ruk_rating_snippet",
  },
} as const;

export const ReviewsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [productQueue, setProductQueue] = useState<string[]>([]);
  const [totalReviewsCount, setTotalReviewsCount] = useState<number>(0);
  const productQueueDebounced = useDebounce<string[]>(productQueue, 250);

  const addProductToQueue = useCallback(
    (handle: string) => {
      return setProductQueue((prevState) => {
        return [...prevState, handle];
      });
    },
    [setProductQueue],
  );

  const renderRatingSnippet = useCallback((): NodeJS.Timeout | void => {
    const ratingSnippet = window["ratingSnippet"];
    if (!ratingSnippet) {
      return setTimeout(() => renderRatingSnippet(), 1000);
    }

    try {
      ratingSnippet(config.snippet.name, {
        store: config.store,
        color: config.snippet.color,
        text: "reviews",
        linebreak: false,
        useCustomLookup: true,
      });
    } catch (e) {
      console.error("Call to ratingSnippet threw an exception.", e);
    }
  }, []);

  const values = useMemo(
    () => ({
      addProductToQueue,
      productQueue: productQueueDebounced,
      totalReviewsCount,
      setTotalReviewsCount,
      renderRatingSnippet,
    }),
    [
      addProductToQueue,
      productQueueDebounced,
      totalReviewsCount,
      renderRatingSnippet,
    ],
  );

  useEffect(() => {
    renderRatingSnippet();
  }, [productQueueDebounced]);

  return (
    <ReviewsContext.Provider value={values}>{children}</ReviewsContext.Provider>
  );
};
